// Styles
import styled from 'styled-components'

// Core
import {CheckCircle} from '@material-ui/icons'
import React, {Fragment} from 'react'

// Components, actions, models, etc.
import {FormHelperText} from '@material-ui/core'
import Button from 'components/Button'
import Checkbox from 'components/Checkbox'
import {PrivacyPolicy, TermsOfService} from 'components/LinkButton'
import {Field} from 'react-final-form'
import {CheckoutSummaryDivider, CheckoutSummaryItem} from './CheckoutSummaryItems'
import {getTotalPriceWithTax} from 'services/cdaTotalPriceServices'

type OrderItemsProps = {
  isMLSEnabled: boolean
  turnTime: number
}

type TotalProps = {
  price: number
}

type CheckoutSummaryProps = OrderItemsProps &
  TotalProps & {
    handleFormSubmit: () => void
    submitting: boolean
    agreementField: string
  }
const CheckoutSummary = ({
  isMLSEnabled,
  turnTime,
  price,
  submitting,
  handleFormSubmit,
  agreementField
}: CheckoutSummaryProps) => {
  return (
    <Fragment>
      <CheckoutSummary.OrderItems turnTime={turnTime} isMLSEnabled={isMLSEnabled} />
      <CheckoutSummaryDivider />
      <CheckoutSummary.Total price={price} />
      <CheckoutSummaryDivider />
      <CheckoutSummary.Agreement field={agreementField} />
      <CheckoutSummary.PlaceOrderButton
        onClick={handleFormSubmit}
        title={submitting ? <span>Buying&hellip;</span> : 'Place Order'}
        type='submit'
        disabled={submitting}
      />
    </Fragment>
  )
}

CheckoutSummary.OrderItems = ({isMLSEnabled, turnTime}: OrderItemsProps) => {
  const items = []
  if (turnTime) {
    items.push(`${turnTime} Day Turn Time`)
  }

  if (isMLSEnabled) {
    items.push('Broker Generated Listing Report')
  }

  return (
    <CheckoutSummary.OrderItemsStyled>
      <h5 className='summary-title'>Includes</h5>
      <ul>
        {items.map(item => (
          <li className='order-item'>
            <CheckCircle />
            {item}
          </li>
        ))}
      </ul>
    </CheckoutSummary.OrderItemsStyled>
  )
}

CheckoutSummary.OrderItemsStyled = styled(CheckoutSummaryItem)`
  flex-direction: column;

  ul {
    padding: 0;
    margin: 0;
  }

  .summary-title {
    margin-bottom: 12px;
  }

  .order-item {
    display: flex;
    column-gap: 8px;
    align-items: center;

    + .order-item {
      margin-top: 4px;
    }
  }

  .portal-MuiSvgIcon-root {
    font-size: 1rem;
    color: ${({theme}) => theme.colors.emerald.base};
  }
`

CheckoutSummary.Total = ({price}: TotalProps) => {
  return (
    <CheckoutSummary.TotalStyled>
      <div className='checkout-summary-wrapper'>
        <h4 className='total-wrapper'>
          Total
          <span className='total-price'>${getTotalPriceWithTax(price)}</span>
        </h4>
        <p className='taxes-message'>Includes taxes and transaction fees</p>
      </div>
    </CheckoutSummary.TotalStyled>
  )
}

CheckoutSummary.TotalStyled = styled(CheckoutSummaryItem)`
  .checkout-summary-wrapper {
    width: 100%;
    .total-wrapper {
      display: flex;
      justify-content: space-between;
    }
    .total-price {
      font-weight: 500;
      font-size: 1rem;
      line-height: 22px;
    }
    .taxes-message {
      text-align: right;
      font-size: 0.75rem;
      color: ${({theme}) => theme.colors.grayscale.gray};
    }
  }

  p {
    margin: 0;
    color: ${({theme}) => theme.colors.grayscale.gray};
    font-size: 0.75rem;
    align-self: end;
    line-height: 17px;
    margin-top: 2px;
  }
`

CheckoutSummary.Agreement = ({field}: {field: string}) => {
  return (
    <Field name={field}>
      {({input, meta}) => {
        return (
          <CheckoutSummary.AgreementStyled>
            <Checkbox
              label={
                <Fragment>
                  I agree to Clear Capital’s&nbsp;
                  <TermsOfService />
                  &nbsp;and&nbsp;
                  <PrivacyPolicy />
                </Fragment>
              }
              checked={input.value}
              onChange={e => {
                input.onChange(e.target.checked)
              }}
            />
            <FormHelperText error>{meta.touched && meta.error}</FormHelperText>
          </CheckoutSummary.AgreementStyled>
        )
      }}
    </Field>
  )
}

CheckoutSummary.AgreementStyled = styled(CheckoutSummaryItem)`
  flex-direction: column;

  .portal-MuiFormControlLabel-root {
    align-items: start;
  }

  .portal-MuiFormHelperText-root {
    position: relative;
    white-space: normal;
  }
`

CheckoutSummary.PlaceOrderButton = styled(Button)`
  width: 100%;
`

export default CheckoutSummary
